/*!
  Pioneer WFRIA InlineFilter v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["InlineFilter"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("InlineFilter") < 0) {
    window.__PIONEER_GLOBAL__.components.push("InlineFilter");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "container", "className", "customStrings", "defaultExpanded", "disabled", "filterApplied", "filtersAppliedIcon", "filtersIcon", "onCancel", "onFilter", "onReset", "resultsAnnouncement", "resultsContainerId", "title"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useUniqueId } from "../common/hooks";
import { validIcon } from "../common/proptypes";
import { getObjectValues } from "../common/utilities";
import { LiveRegion } from "../private";
import ExpandableTransition from "../private/ExpandableTransition/ExpandableTransition";
import Tooltip from "../private/Tooltip/Tooltip";
import { ANNOUNCEMENT_TIMEOUT, CLEAR_ANNOUNCEMENT_TIMEOUT, EXPANDABLE_TRANSITION_TIMEOUT, MOBILE_BREAKPOINT } from "./constants";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
import { Button, Fieldset, Form, IconFilter, IconFilterApplied } from "..";
/**
 * Brief description of the InlineFilter component.
 * Please use the blurb found at the top of the
 * component specifications.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const InlineFilter = /*#__PURE__*/React.forwardRef((_ref, inlineFilterRef) => {
  let {
    children,
    container,
    className,
    customStrings,
    defaultExpanded,
    disabled,
    filterApplied: filterAppliedProp,
    filtersAppliedIcon,
    filtersIcon,
    onCancel,
    onFilter,
    onReset,
    resultsAnnouncement,
    resultsContainerId,
    title
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const initialState = {
    filterApplied: filterAppliedProp,
    filterReset: false,
    disableApplyButton: true,
    disableResetButton: true
  };
  const [filterState, setFilterState] = useState(initialState);
  const [open, setOpen] = useState(defaultExpanded);
  const inlineFilterContainer = useRef(document.createElement("div")); // The `previousFilter` object contains the previously applied form field data.

  const previousFilter = useRef(null); // The `currentFilter` object contains the latest form field data.

  const currentFilter = useRef(null);
  /**
   * This flag is used to update the `initialRender` values with default
   * values by considering `filterApplied` prop.
   */

  const defaultFilterAppliedCheck = useRef(false);
  const applyButtonWasClicked = useRef(false);
  const statusAnnouncement = useRef("");
  const announcementKey = useUniqueId();

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const initialRender = useRef({});
  const uniqueId = useUniqueId();
  const theme = useTheme();
  const {
    cssRoot,
    cssBodyContainer,
    cssButtonsContainer,
    cssResetContainer,
    cssFilterButton
  } = extractTheme(_objectSpread({
    theme,
    filterApplied: filterAppliedProp !== null ? filterAppliedProp : filterState.filterApplied
  }, otherProps));
  const formRef = useRef();
  const applyButton = useRef();
  const cancelButton = useRef();
  const filtersButton = useRef();
  /**
   * The `Apply` button submits the InlineFilter form, which executes the `handleFilter` function
   * and subsequently executes the passed `onFilter` function with an updated `resultsAnnouncement` prop.
   * The updated `statusAnnouncement` prop is defined here, and is used in the `LiveRegion` component below.
   */

  let currentResultsAnnouncement = resultsAnnouncement,
      inlineFilterRootNode = null;
  /**
   * @description Loops through all the form fields in the InlineFilter,
   * and produces an object containing each field's identifier and its corresponding value.
   * @return {Object} Returns an object.
   */

  const getFilter = () => {
    const filterObject = formRef.current.getValues(); // To remove hidden fields, which're used for fieldset logic

    Object.keys(filterObject).filter(key => !key.indexOf("pnr-fieldset-hiddeninput")).forEach(key => delete filterObject[key]);
    return filterObject;
  };
  /**
   * @description Stores the initial form field values into the `initialRender` property.
   */


  const setInitialFilterValues = () => {
    previousFilter.current = getFilter();
    /**
     * When filter values are updated in CDM life cycle of the parent component.
     * either by async or setState, `initialRender` should not be updated
     * because we will be using those values while resetting the filter fields.
     */

    if (!filterAppliedProp) {
      initialRender.current = _objectSpread({}, previousFilter.current);
    }
  };
  /**
   * @description
   * Appends the `inlineFilterContainer` to the `inlineFilterRootNode`.
   */


  const renderRootContainer = () => {
    if (container && open && !inlineFilterRootNode) {
      inlineFilterRootNode = document.querySelector(container);

      if (inlineFilterRootNode) {
        inlineFilterRootNode.appendChild(inlineFilterContainer.current);
      }
    }
  };
  /**
   * @description Returns stringified values of a given object.
   * @param {Object} `currentFilter` or `previousFilter` object.
   * @return {String} Returns a string.
   */


  const getFilterValues = filterObject => Object.values ? JSON.stringify(Object.values(filterObject)) : JSON.stringify(getObjectValues(filterObject));
  /**
   * @description Compares the current filter values with the initial filter values.
   * Rerurns `true` if the filters are equal, and `false` otherwise
   * @return {Boolean}
   */


  const getResetButtonState = () => currentFilter.current && initialRender.current && getFilterValues(currentFilter.current) === getFilterValues(initialRender.current);
  /**
   * @description
   * Sets the focus to the "Apply" button if `filterApplied` is true.
   * Sets the focus to the "Cancel" button if `filterApplied` is false.
   */


  const setFocus = () => {
    const {
      disableApplyButton,
      filterApplied
    } = filterState;

    if (filterApplied && !disableApplyButton) {
      applyButton.current.focus();
    } else {
      cancelButton.current.focus();
    }
    /**
     * The `filterReset` state should be reset to its default value after setting the focus.
     */


    setFilterState(_objectSpread(_objectSpread({}, filterState), {}, {
      filterReset: false
    }));
  };
  /**
   * @description This function is executed when the validity of the form changes.
   * It disables the "Apply" button any time the validity state of the form is changed.
   */


  const handleValidate = () => {
    setFilterState(_objectSpread(_objectSpread({}, filterState), {}, {
      disableApplyButton: true
    }));
  };
  /**
   * @description Handles the click event of the "Apply" button.
   * Executes the passed `onFilter` function if it exists. Then,
   *   - Closes the InlineFilter body.
   *   - Disables the "Apply" button.
   *   - Focuses on the "Filters" button.
   * @param {EventObject} e The event facade of the click event.
   */


  const handleFilter = e => {
    let isFilterApplied = filterAppliedProp;
    previousFilter.current = getFilter();
    /**
     * Execute onFilter to filter the data.
     */

    if (onFilter) {
      onFilter(e);
    }
    /**
     * If the `filterApplied` prop is not passed by the user.
     */


    if (isFilterApplied === null) {
      /*
       * Switch the state of the "Filters" button by comparing initial field values with
       * applied filter field values.
       */
      for (const key in previousFilter.current) {
        if (initialRender.current[key] !== previousFilter.current[key]) {
          isFilterApplied = true;
        }
      }
    }

    applyButtonWasClicked.current = true;
    setFilterState(_objectSpread(_objectSpread({}, filterState), {}, {
      filterApplied: isFilterApplied,
      disableApplyButton: true,
      filterReset: false
    }));
    setOpen(false);
    filtersButton.current.focus();
    e.preventDefault();
  };
  /**
   * @description Handles the click event of the "Reset Filters" button.
   * Executes the passed `onReset` prop if it exists. Then,
   *   - Updates the status announcement message.
   *   - Resets the form fields to their initial state by invoking the 'onReset' prop.
   * @param {EventObject} e The event facade of the click event.
   */


  const handleReset = e => {
    if (onReset) {
      onReset(e);
    }

    currentFilter.current = _objectSpread({}, initialRender.current);
    statusAnnouncement.current = filterState.filterApplied ? componentStrings.resetButtonAnnouncement : componentStrings.resetButtonDefaultAnnouncement;
    const isFilterApplied = filterAppliedProp !== null ? filterAppliedProp : filterState.filterApplied;
    setFilterState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
      disableApplyButton: formRef.current.formState.isValid,
      filterReset: true,
      filterApplied: isFilterApplied
    }));
  };
  /**
   * Resets the InlineFilter dynamically.
   * @description Upon execution:
   *   - Reset the form fields.
   *   - Resets the buttons to their initially rendered state.
   *   - Disables the "Apply" button.
   *   - Disables the "Reset" button.
   * @public
   */


  const reset = () => {
    formRef.current.reset();
    currentFilter.current = _objectSpread({}, initialRender.current);
    setFilterState(_objectSpread({}, initialState));
  };
  /**
   * @description Handles the click event of the "Cancel" button.
   * Executes the passed `onCancel` prop if it exists. Then,
   *   - Updates the status announcement message.
   *   - Closes the InlineFilter body.
   *   - Focuses on the "Filters" button.
   * @param {EventObject} e The event facade of the click event.
   */


  const handleCancel = e => {
    currentFilter.current = _objectSpread({}, previousFilter.current);

    if (onCancel) {
      /**
       * Explicitly sets the focus to the "Cancel" button.
       * This is to fix the Voiceover focus issue.
       */
      cancelButton.current.focus();
      onCancel(e);
    }

    statusAnnouncement.current = componentStrings.cancelButtonAnnouncement;
    setFilterState(_objectSpread(_objectSpread({}, filterState), {}, {
      disableApplyButton: true,
      filterReset: false
    }));
    setOpen(false);
    filtersButton.current.focus();
  };
  /**
   * @description Toggles the InlineFilter.
   * Toggles the InlineFilter between open and closed states.
   */


  const toggleFilter = () => {
    setFilterState(_objectSpread(_objectSpread({}, filterState), {}, {
      filterReset: false
    }));
    setOpen(!open);

    if (window.innerWidth < MOBILE_BREAKPOINT) {
      filtersButton.current.scrollIntoView(true);
    }
  };

  useImperativeHandle(inlineFilterRef, () => ({
    reset: () => reset(),
    formRef: formRef.current
  }));
  useEffect(() => {
    setInitialFilterValues();
    renderRootContainer();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    renderRootContainer();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Shift the focus when the "Reset Filters" button is clicked.
    if (filterState.filterReset) {
      setFocus();
    }
  }, [filterState.disableResetButton]); // eslint-disable-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    const {
      disableApplyButton: disableApplyButtonState,
      disableResetButton: disableResetButtonState,
      filterReset,
      filterApplied
    } = filterState;
    const isFilterApplied = filterAppliedProp !== null ? filterAppliedProp : filterApplied;

    if (open) {
      currentFilter.current = filterReset ? _objectSpread({}, initialRender.current) : getFilter();
      /**
       * Changing initialRender with default values by considering
       * `defaultFilterAppliedCheck` flag and toggles its value which makes
       * to execute this block only once.
       */

      if (!defaultFilterAppliedCheck.current && filterAppliedProp) {
        defaultFilterAppliedCheck.current = true; // to set default filter values as initial filter values

        setInitialFilterValues();
      }

      const disableResetButton = getResetButtonState(); // Set the `disableResetButton` state based on current filter values.

      if (disableResetButtonState !== disableResetButton) {
        /**
         * `setState` can be called immediately in componentDidUpdate() as long as it is wrapped
         * in a condition, or an infinite loop is caused.
         * `eslint` does not check whether `setState` is wrapped
         * inside a condition, therefore it throws an error.
         * Hence, disabling the `eslint` rule.
         */
        // eslint-disable-next-line react/no-did-update-set-state
        setFilterState(_objectSpread(_objectSpread({}, filterState), {}, {
          disableResetButton
        }));
      }

      let disableApplyButton;
      const {
        isDirty,
        isValid
      } = formRef.current.formState;

      if ((previousFilter.current && getFilterValues(previousFilter.current)) === (currentFilter.current && getFilterValues(currentFilter.current))) {
        disableApplyButton = true;
      } else {
        disableApplyButton = isFilterApplied ? !isValid : !isValid || !isDirty;
      }
      /**
       * On field change verify the form validity and set the 'Apply' button status.
       * Irrespective of the Form validity state, the "Apply" button should be disabled
       * when the InlineFilter is expanded. On change of any form field, check the
       * validity state and set the 'Apply' button state.
       */


      if (disableApplyButtonState !== disableApplyButton) {
        // eslint-disable-next-line react/no-did-update-set-state
        setFilterState(_objectSpread(_objectSpread({}, filterState), {}, {
          disableApplyButton
        }));
      }
    }
  });

  const handleAnnouncementClear = () => {
    statusAnnouncement.current = "";
  };

  const {
    disableApplyButton,
    disableResetButton,
    filterApplied
  } = filterState;
  const filterAppliedState = filterAppliedProp !== null ? filterAppliedProp : filterApplied;
  const {
    applyButtonLabel,
    applyButtonAriaLabel,
    cancelButtonLabel,
    cancelButtonAriaLabel,
    filtersButtonLabel,
    filtersAppliedButtonLabel,
    resetFiltersButtonLabel,
    resetFiltersButtonAriaLabel
  } = componentStrings;
  /**
   * Support deprecated `customStrings.resultsAnnouncement`:
   *
   * If the application has not passed in a value for the `resultsAnnouncement`
   * prop, then test if one was added via `customStrings.resultsAnnouncement`.
   */

  if (Boolean(resultsAnnouncement) === false && customStrings && customStrings.resultsAnnouncement) {
    currentResultsAnnouncement = customStrings.resultsAnnouncement;
  }

  if (applyButtonWasClicked.current && currentResultsAnnouncement) {
    statusAnnouncement.current = currentResultsAnnouncement;
    applyButtonWasClicked.current = false;
  }

  const wrapperClassNames = clsx("pnr-inline-filter", {
    "pnr-inline-filter--filter-applied": filterAppliedState
  }, className);
  const customFiltersIcon = filtersIcon ? filtersIcon : _jsx(IconFilter, {
    size: "small",
    decorative: true
  });
  const customFiltersAppliedIcon = filtersAppliedIcon ? filtersAppliedIcon : _jsx(IconFilterApplied, {
    size: "small",
    decorative: true
  });
  const inlineFilterIcon = filterAppliedState ? customFiltersAppliedIcon : customFiltersIcon;
  const filterToggleId = `pnr-inline-filter__toggleFilter--${uniqueId}`;
  const filterBodyId = `pnr-inline-filter__body-${uniqueId}`;

  const filterBody = _jsx(ExpandableTransition, {
    expanded: open,
    duration: EXPANDABLE_TRANSITION_TIMEOUT,
    css: cssBodyContainer,
    className: "pnr-inline-filter__body",
    id: filterBodyId,
    children: _jsx(Form, {
      onValidate: handleValidate,
      onSubmit: handleFilter,
      ref: formRef,
      children: _jsxs(Fieldset, {
        legend: title,
        className: "pnr-inline-filter__fieldset",
        fieldsetBodyClassName: "pnr-inline-filter__fieldset-body",
        children: [children, _jsx("div", {
          className: "pnr-inline-filter__resetbutton-container",
          css: cssResetContainer,
          children: _jsx(Button, {
            kind: "standard",
            slim: true,
            type: "button",
            onClick: handleReset,
            disabled: disableResetButton,
            "aria-label": resetFiltersButtonAriaLabel,
            children: resetFiltersButtonLabel
          })
        }), _jsxs("div", {
          className: "pnr-inline-filter__buttons-container",
          css: cssButtonsContainer,
          children: [_jsx(Button, {
            disabled: disableApplyButton,
            className: "pnr-inline-filter__apply-button",
            css: cssButtonsContainer.applyButton,
            ref: applyButton,
            type: "submit",
            "aria-controls": resultsContainerId ? resultsContainerId : null,
            "aria-label": applyButtonAriaLabel,
            children: applyButtonLabel
          }), _jsx(Button, {
            kind: "standard",
            onClick: handleCancel,
            ref: cancelButton,
            "aria-controls": filterToggleId,
            "aria-label": cancelButtonAriaLabel,
            children: cancelButtonLabel
          })]
        })]
      })
    })
  });

  return _jsxs("div", _objectSpread(_objectSpread({
    className: wrapperClassNames
  }, otherProps), {}, {
    css: cssRoot,
    children: [_jsx(LiveRegion, {
      politeness: "polite",
      announcementDelay: ANNOUNCEMENT_TIMEOUT,
      clearAnnouncementDelay: CLEAR_ANNOUNCEMENT_TIMEOUT,
      onAnnouncementClear: handleAnnouncementClear,
      children: statusAnnouncement.current
    }, announcementKey), _jsx(Tooltip, {
      autoPlacement: true,
      label: filterAppliedState ? filtersAppliedButtonLabel : filtersButtonLabel,
      trigger: _jsx(Button, {
        id: filterToggleId,
        kind: "standard",
        onClick: toggleFilter,
        ref: filtersButton,
        leftIcon: inlineFilterIcon,
        "aria-label": filterAppliedState ? filtersAppliedButtonLabel : filtersButtonLabel,
        buttonStyles: cssFilterButton,
        className: filterAppliedState ? "pnr-inline-filter__applied" : "pnr-inline-filter__filters",
        "aria-controls": open ? filterBodyId : null,
        "aria-expanded": open,
        disabled: disabled,
        children: _jsx("span", {
          "aria-hidden": "true",
          children: filterAppliedState ? filtersAppliedButtonLabel : filtersButtonLabel
        })
      }),
      children: filterAppliedState ? filtersAppliedButtonLabel : filtersButtonLabel
    }), container ? /*#__PURE__*/ReactDOM.createPortal(filterBody, inlineFilterContainer.current) : filterBody]
  }));
});
InlineFilter.propTypes = process.env.NODE_ENV !== "production" ? {
  /** The body of the InlineFilter that should contain form fields. */
  children: PropTypes.node.isRequired,
  className: PropTypes.string,

  /** The container into which the body of InlineFilter component will be rendered. */
  container: PropTypes.string,

  /** Override for strings used within the component. */
  customStrings: PropTypes.shape({
    /**
     * Apply button label.
     * Defaults to "Apply".
     */
    applyButtonLabel: PropTypes.string,

    /**
     * The aria-label of the "Apply" button.
     * Defaults to "apply filters".
     */
    applyButtonAriaLabel: PropTypes.string,

    /**
     * Reset Filters button label.
     * Defaults to "Reset Filters".
     */
    resetFiltersButtonLabel: PropTypes.string,

    /**
     * The aria-label of the "Reset" button.
     * Defaults to "reset filters to default".
     */
    resetFiltersButtonAriaLabel: PropTypes.string,

    /**
     * Cancel button label.
     * Defaults to "Cancel".
     */
    cancelButtonLabel: PropTypes.string,

    /**
     * The aria-label of the "Cancel" button.
     * Defaults to "cancel changes to filter".
     */
    cancelButtonAriaLabel: PropTypes.string,

    /**
     * Filters button label.
     * Defaults to "Filters".
     */
    filtersButtonLabel: PropTypes.string,

    /**
     * Filters Applied button label.
     * Defaults to "Filters Applied".
     */
    filtersAppliedButtonLabel: PropTypes.string,

    /**
     * Text that will be announced by the screen readers when the "Reset Filters" button is clicked.
     * Defaults to "filters have been reset, reset button disabled, click apply to reset results".
     */
    resetButtonAnnouncement: PropTypes.string,

    /**
     * Text that will be announced by the screen readers when the "Reset Filters" button
     * is clicked and the filters have not been applied yet.
     * Defaults to "filters have been reset, reset button disabled".
     */
    resetButtonDefaultAnnouncement: PropTypes.string,

    /**
     * Text that will be announced by the screen readers when the "Cancel" button is clicked.
     * Defaults to "changes to filter have been cancelled".
     */
    cancelButtonAnnouncement: PropTypes.string,

    /**
     * Text that will be announced by the screen readers when the "Apply" button is clicked.
     * Defaults to empty string.
     * <strong>Deprecated:</strong> As of release 3.27.1, this prop has been moved out of `customStrings`.
     */
    resultsAnnouncement: PropTypes.string
  }),

  /** Prop to expand the InlineFilter container body on initial render. */
  defaultExpanded: PropTypes.bool,

  /** Prop to disable the InlineFilter Button. */
  disabled: PropTypes.bool,

  /**
   * Determines whether the InlineFilter shows up in the `Filters Applied` state or not.
   * Use this prop in cases where the `Filters Applied` state should be controlled.
   */
  filterApplied: PropTypes.bool,

  /**
   * Provides a custom Icon type for the "Filters Applied" button. See Iconography section.
   * NOTE: Default props, such as `size` and `decorative`, are not provided automatically.
   * @ignore
   */
  filtersAppliedIcon: validIcon,

  /**
   * Provides a custom Icon type for the "Filters" button. See Iconography section.
   * NOTE: Default props, such as `size` and `decorative`, are not provided automatically.
   * @ignore
   */
  filtersIcon: validIcon,

  /**
   * Callback handler to be executed on click of the "Cancel" button.
   * @param {EventFacade} event Click event object.
   */
  onCancel: PropTypes.func,

  /**
   * Callback handler to be executed on click of the "Apply" button.
   * @param {EventFacade} event Click event object.
   */
  onFilter: PropTypes.func,

  /**
   * Callback handler to be executed on click of the "Reset" button.
   * @param {EventFacade} event Click event object.
   */
  onReset: PropTypes.func,

  /**
   * Text that will be announced by the screen readers when the "Apply" button is clicked.
   */
  resultsAnnouncement: PropTypes.string,

  /**
   * This ID is added to the `aria-controls` attribute of the `Apply` button.
   * It should be the same ID of the element that will contain the results produced by the `Apply` button.
   * i.e. When Inline Filter is used with a table, it would be the ID of the Table.
   */
  resultsContainerId: PropTypes.string,

  /**
   * InlineFilter title.
   * Defaults to "Filter criteria".
   */
  title: PropTypes.string
} : {};
InlineFilter.defaultProps = {
  className: "",
  container: null,
  filterApplied: null,
  defaultExpanded: false,
  disabled: false,
  onFilter: null,
  onReset: null,
  onCancel: null,
  title: STRINGS.defaultInlineFilterLegend,
  filtersIcon: null,
  filtersAppliedIcon: null,
  customStrings: {},
  resultsContainerId: null,
  resultsAnnouncement: null,
  display: "block",
  spacing: {}
};
InlineFilter.displayName = "InlineFilter";
export default InlineFilter;